import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { 
  DollarSign, TrendingUp, TrendingDown, Calendar, Plus, 
  Building2, FileText, Filter, Search, X, CheckCircle2,
  Clock, AlertCircle, CreditCard, Wallet, Banknote,
  Package, ShoppingCart, Truck, Download, BarChart3,
  ShoppingBag,
  Users, LayoutGrid, List
} from 'lucide-react';
import toast from 'react-hot-toast';
import io from 'socket.io-client';
import './Financeiro.css';

const Financeiro = () => {
  const [abaAtiva, setAbaAtiva] = useState('pagar');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalFornecedor, setModalFornecedor] = useState(false);
  const [modalLancamento, setModalLancamento] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('pendentes');
  const [busca, setBusca] = useState('');
  const queryClient = useQueryClient();

  // Estados para Relatórios
  const [tipoRelatorio, setTipoRelatorio] = useState('vendas');
  const [agrupamento, setAgrupamento] = useState('dia');
  const [dataInicio, setDataInicio] = useState(new Date().toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [buscaRelatorio, setBuscaRelatorio] = useState('');
  const [filtroStatusRelatorio, setFiltroStatusRelatorio] = useState('todos');
  const [filtroPagamentoRelatorio, setFiltroPagamentoRelatorio] = useState('todos');
  const [limiteProdutos, setLimiteProdutos] = useState(20);
  const [visualizacao, setVisualizacao] = useState('card');

  // Conectar Socket.io para atualizações em tempo real
  useEffect(() => {
    // Usar mesma lógica do api.js para determinar URL
    let socketURL;
    if (process.env.NODE_ENV === 'production') {
      socketURL = typeof window !== 'undefined' ? window.location.origin : '';
    } else {
      socketURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }

    const socket = io(socketURL, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/'
    });
    
    socket.on('connect', () => {
      console.log('✅ Conectado ao Socket.io no Financeiro');
    });

    socket.on('nova-conta-pagar', (data) => {
      toast.success(`💰 Nova conta a pagar: ${data.entregador || 'Entregador'} - R$ ${data.valor.toFixed(2)}`, {
        icon: '💰',
        duration: 5000
      });
      queryClient.invalidateQueries('contas-financeiro');
    });

    socket.on('conta-paga', (data) => {
      queryClient.invalidateQueries('contas-financeiro');
    });

    return () => {
      socket.disconnect();
    };
  }, [queryClient]);

  // Buscar estabelecimento
  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-financeiro',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        console.error('Erro ao buscar estabelecimento:', error);
        return null;
      }
    }
  );

  const estabelecimentoId = estabelecimentoData?._id;

  // Buscar contas
  const { data: contas } = useQuery(
    ['contas-financeiro', estabelecimentoId, filtroStatus],
    async () => {
      if (!estabelecimentoId) {
        console.log('⚠️ Estabelecimento ID não encontrado');
        return { contas: [] };
      }
      try {
        const params = new URLSearchParams();
        params.append('estabelecimento', estabelecimentoId);
        if (filtroStatus === 'pagos') params.append('status', 'pago');
        if (filtroStatus === 'pendentes') params.append('status', 'aberto');
        
        console.log('📤 Buscando contas com params:', params.toString());
        const response = await api.get(`/api/financeiro/contas?${params}`);
        console.log('📥 Resposta completa da API:', response);
        console.log('📊 response.data:', response.data);
        console.log('📊 response.data.data:', response.data.data);
        console.log('📊 response.data.data.contas:', response.data.data?.contas);
        console.log('📊 Quantidade de contas:', response.data.data?.contas?.length || 0);
        
        const resultado = response.data.data || { contas: [] };
        console.log('✅ Retornando:', resultado);
        return resultado;
      } catch (error) {
        console.error('❌ Erro ao buscar contas:', error);
        console.error('❌ Erro completo:', error.response?.data || error.message);
        return { contas: [] };
      }
    },
    {
      enabled: !!estabelecimentoId,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );
  
  // ✅ DEBUG: Log do que foi retornado
  useEffect(() => {
    console.log('🔍 DEBUG - contas após useQuery:', contas);
    console.log('🔍 DEBUG - contas?.contas:', contas?.contas);
    console.log('🔍 DEBUG - Array.isArray(contas?.contas):', Array.isArray(contas?.contas));
  }, [contas]);

  // Buscar fornecedores
  const { data: fornecedores } = useQuery(
    ['fornecedores-financeiro', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return { fornecedores: [] };
      try {
        const response = await api.get(`/api/compras/fornecedores?estabelecimento=${estabelecimentoId}`);
        return response.data.data || { fornecedores: [] };
      } catch (error) {
        return { fornecedores: [] };
      }
    },
    {
      enabled: !!estabelecimentoId
    }
  );

  // Estados para filtros do Fluxo de Caixa
  const [dataInicioFluxo, setDataInicioFluxo] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Últimos 30 dias
    return date.toISOString().split('T')[0];
  });
  const [dataFimFluxo, setDataFimFluxo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [filtroOrigemFluxo, setFiltroOrigemFluxo] = useState('todos');

  // Buscar fluxo de caixa
  const { data: fluxoCaixa } = useQuery(
    ['fluxo-caixa', estabelecimentoId, dataInicioFluxo, dataFimFluxo],
    async () => {
      if (!estabelecimentoId) return { movimentacoes: [], saldoAtual: 0, resumo: {} };
      try {
        const params = new URLSearchParams();
        params.append('estabelecimento', estabelecimentoId);
        if (dataInicioFluxo) params.append('dataInicio', dataInicioFluxo);
        if (dataFimFluxo) params.append('dataFim', dataFimFluxo);
        const response = await api.get(`/api/financeiro/fluxo-caixa?${params}`);
        return response.data.data || { movimentacoes: [], saldoAtual: 0, resumo: {} };
      } catch (error) {
        console.error('Erro ao buscar fluxo de caixa:', error);
        return { movimentacoes: [], saldoAtual: 0, resumo: {} };
      }
    },
    {
      enabled: !!estabelecimentoId && abaAtiva === 'fluxo',
      refetchInterval: 60000 // Atualizar a cada minuto
    }
  );

  // Buscar resumo mensal (Vendas Totais e Valor Líquido)
  const { data: resumoMensal } = useQuery(
    ['resumo-mensal', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return null;
      try {
        const response = await api.get(`/api/financeiro/resumo-mensal?estabelecimento=${estabelecimentoId}`);
        return response.data.data || null;
      } catch (error) {
        console.error('Erro ao buscar resumo mensal:', error);
        return null;
      }
    },
    {
      enabled: !!estabelecimentoId,
      refetchInterval: 60000, // Atualizar a cada minuto
      refetchOnWindowFocus: true
    }
  );

  // Buscar lançamentos financeiros
  const { data: lancamentosData, refetch: refetchLancamentos } = useQuery(
    ['lancamentos-financeiro', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return { lancamentos: [] };
      try {
        const response = await api.get(`/api/financeiro/lancamentos?estabelecimento=${estabelecimentoId}`);
        return response.data.data || { lancamentos: [] };
      } catch (error) {
        console.error('Erro ao buscar lançamentos:', error);
        return { lancamentos: [] };
      }
    },
    {
      enabled: !!estabelecimentoId && abaAtiva === 'lancamentos',
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  const [modalDetalhes, setModalDetalhes] = useState(null);

  // Queries para Relatórios
  const { data: relatorioVendas, isLoading: loadingVendas } = useQuery(
    ['relatorio-vendas', dataInicio, dataFim, agrupamento, estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return null;
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimentoId);
      params.append('dataInicio', dataInicio);
      params.append('dataFim', dataFim);
      params.append('agrupamento', agrupamento);
      params.append('_ts', Date.now().toString());
      const response = await api.get(`/api/faturamento/relatorio/vendas?${params}`);
      return response.data.data;
    },
    {
      enabled: tipoRelatorio === 'vendas' && !!estabelecimentoId && abaAtiva === 'relatorio',
      refetchOnWindowFocus: true,
      refetchOnMount: 'always',
      staleTime: 0,
      refetchInterval: 60000
    }
  );

  const { data: relatorioProdutos, isLoading: loadingProdutos } = useQuery(
    ['relatorio-produtos', dataInicio, dataFim, limiteProdutos, estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return null;
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimentoId);
      params.append('dataInicio', dataInicio);
      params.append('dataFim', dataFim);
      params.append('limite', limiteProdutos);
      const response = await api.get(`/api/faturamento/relatorio/produtos?${params}`);
      return response.data.data;
    },
    { enabled: tipoRelatorio === 'produtos' && !!estabelecimentoId && abaAtiva === 'relatorio' }
  );

  const { data: relatorioPedidos, isLoading: loadingPedidos } = useQuery(
    ['relatorio-pedidos', dataInicio, dataFim, filtroStatusRelatorio, filtroPagamentoRelatorio, buscaRelatorio, estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return null;
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimentoId);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (filtroStatusRelatorio !== 'todos') params.append('status', filtroStatusRelatorio);
      if (filtroPagamentoRelatorio !== 'todos') params.append('statusPagamento', filtroPagamentoRelatorio);
      
      const response = await api.get(`/api/pedidos?${params}`);
      let pedidos = response.data.data.pedidos || [];
      
      if (buscaRelatorio) {
        const buscaLower = buscaRelatorio.toLowerCase();
        pedidos = pedidos.filter(p => 
          p.numero?.toString().includes(buscaLower) ||
          p.cliente?.nome?.toLowerCase().includes(buscaLower) ||
          p.cliente?.telefone?.includes(buscaLower)
        );
      }
      
      return { pedidos };
    },
    { enabled: tipoRelatorio === 'pedidos' && !!estabelecimentoId && abaAtiva === 'relatorio' }
  );

  // Filtrar contas
  const todasContas = contas?.contas || [];
  const contasPagar = todasContas.filter(c => c.tipo === 'pagar');
  const contasReceber = todasContas.filter(c => c.tipo === 'receber');
  
  // Aplicar filtro de status
  let contasFiltradasPagar = contasPagar;
  let contasFiltradasReceber = contasReceber;
  
  if (filtroStatus === 'pendentes') {
    const statusEmAberto = ['pendente', 'parcial', 'vencido'];
    contasFiltradasPagar = contasPagar.filter(c => statusEmAberto.includes(c.status));
    contasFiltradasReceber = contasReceber.filter(c => statusEmAberto.includes(c.status));
  } else if (filtroStatus === 'pagos') {
    contasFiltradasPagar = contasPagar.filter(c => c.status === 'pago');
    contasFiltradasReceber = contasReceber.filter(c => c.status === 'pago');
  }
  
  // Contas pagas para relatório (sempre mostrar todas as pagas)
  const contasPagas = todasContas.filter(c => c.status === 'pago');
  
  // Aplicar busca
  if (busca) {
    const buscaLower = busca.toLowerCase();
    contasFiltradasPagar = contasFiltradasPagar.filter(c => 
      c.descricao?.toLowerCase().includes(buscaLower) ||
      c.fornecedor?.toLowerCase().includes(buscaLower) ||
      c.categoria?.toLowerCase().includes(buscaLower)
    );
    contasFiltradasReceber = contasFiltradasReceber.filter(c => 
      c.descricao?.toLowerCase().includes(buscaLower) ||
      c.cliente?.toLowerCase().includes(buscaLower) ||
      c.categoria?.toLowerCase().includes(buscaLower)
    );
  }

  // Calcular totais
  const totalPagar = contasPagar
    .filter(c => c.status !== 'pago')
    .reduce((acc, c) => acc + (c.valor - (c.valorPago || 0)), 0);
  const totalReceber = contasReceber
    .filter(c => c.status !== 'pago')
    .reduce((acc, c) => acc + (c.valor - (c.valorPago || 0)), 0);

  return (
    <div className="financeiro-page">
      <div className="page-header">
        <div>
          <h2>💼 Financeiro</h2>
          <p>Gestão financeira completa e profissional</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setModalFornecedor(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Building2 size={18} />
            Fornecedores
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setModalLancamento(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <DollarSign size={18} />
            Novo Lançamento
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setModalAberto(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            Nova Conta
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="resumo-financeiro">
        <div 
          className="resumo-card vendas-totais" 
          onClick={() => setModalDetalhes('entradas')}
          style={{ cursor: 'pointer' }}
        >
          <div className="card-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <TrendingUp size={28} />
          </div>
          <div>
            <span>Entradas Totais (Mês)</span>
            <strong>R$ {resumoMensal?.entradasTotais?.toFixed(2) || resumoMensal?.vendasTotais?.toFixed(2) || '0.00'}</strong>
            <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
              {resumoMensal?.resumo?.quantidadePedidos || 0} pedidos + {resumoMensal?.resumo?.quantidadeVendas || 0} vendas + {resumoMensal?.resumo?.quantidadeReceitas || 0} receitas
            </small>
          </div>
        </div>
        <div 
          className="resumo-card valor-liquido" 
          onClick={() => setModalDetalhes('liquido')}
          style={{ cursor: 'pointer' }}
        >
          <div className="card-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <DollarSign size={28} />
          </div>
          <div>
            <span>Valor Líquido (Mês)</span>
            <strong>R$ {resumoMensal?.valorLiquido?.toFixed(2) || '0.00'}</strong>
            <small style={{ fontSize: '0.75rem', opacity: 0.8 }}>
              Após descontos, pagamentos e lançamentos
            </small>
          </div>
        </div>
        <div className="resumo-card receber">
          <div className="card-icon">
            <TrendingUp size={28} />
          </div>
          <div>
            <span>A Receber</span>
            <strong>R$ {totalReceber.toFixed(2)}</strong>
          </div>
        </div>
        <div className="resumo-card pagar">
          <div className="card-icon">
            <TrendingDown size={28} />
          </div>
          <div>
            <span>A Pagar</span>
            <strong>R$ {totalPagar.toFixed(2)}</strong>
          </div>
        </div>
        <div className="resumo-card saldo">
          <div className="card-icon">
            <DollarSign size={28} />
          </div>
          <div>
            <span>Saldo Atual</span>
            <strong>R$ {fluxoCaixa?.saldoAtual?.toFixed(2) || '0.00'}</strong>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="abas-financeiro">
        <button
          className={`aba ${abaAtiva === 'pagar' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('pagar')}
        >
          <CreditCard size={18} />
          Contas a Pagar
        </button>
        <button
          className={`aba ${abaAtiva === 'receber' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('receber')}
        >
          <Wallet size={18} />
          Contas a Receber
        </button>
        <button
          className={`aba ${abaAtiva === 'fluxo' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('fluxo')}
        >
          <TrendingUp size={18} />
          Fluxo de Caixa
        </button>
        <button
          className={`aba ${abaAtiva === 'lancamentos' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('lancamentos')}
        >
          <DollarSign size={18} />
          Lançamentos
        </button>
      </div>

      {/* Filtros e Busca */}
      {(abaAtiva === 'pagar' || abaAtiva === 'receber') && (
        <div className="filtros-financeiro">
          <div className="busca-container">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por descrição, fornecedor, categoria..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="input-busca"
            />
            {busca && (
              <button 
                onClick={() => setBusca('')}
                className="btn-clear-busca"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="filtro-status">
            <Filter size={18} />
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="select-filtro"
            >
              <option value="todas">Todas</option>
              <option value="pendentes">Pendentes</option>
              <option value="pagos">Pagas</option>
            </select>
          </div>
        </div>
      )}

      {/* Conteúdo das Abas */}
      <div className="conteudo-aba">
        {abaAtiva === 'pagar' && (
          <div className="contas-container">
            {contasFiltradasPagar.length > 0 ? (
              <div className="contas-lista">
                {contasFiltradasPagar
                  .filter(c => c.categoria === 'Entregadores')
                  .length > 0 && (
                  <div className="categoria-grupo">
                    <h4 className="categoria-titulo entregadores">
                      🚴 Entregadores
                    </h4>
                    <div className="contas-grid">
                      {contasFiltradasPagar
                        .filter(c => c.categoria === 'Entregadores')
                        .map(conta => (
                          <ContaCard key={conta._id} conta={conta} tipo="pagar" />
                        ))}
                    </div>
                  </div>
                )}
                {contasFiltradasPagar
                  .filter(c => c.categoria !== 'Entregadores')
                  .length > 0 && (
                  <div className="categoria-grupo">
                    {contasFiltradasPagar.filter(c => c.categoria === 'Entregadores').length > 0 && (
                      <h4 className="categoria-titulo">Outras Contas</h4>
                    )}
                    <div className="contas-grid">
                      {contasFiltradasPagar
                        .filter(c => c.categoria !== 'Entregadores')
                        .map(conta => (
                          <ContaCard key={conta._id} conta={conta} tipo="pagar" />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <CreditCard size={64} />
                <p>Nenhuma conta a pagar encontrada</p>
              </div>
            )}
          </div>
        )}

        {abaAtiva === 'receber' && (
          <div className="contas-container">
            {contasFiltradasReceber.length > 0 ? (
              <div className="contas-lista">
                <div className="contas-grid">
                  {contasFiltradasReceber.map(conta => (
                    <ContaCard key={conta._id} conta={conta} tipo="receber" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <Wallet size={64} />
                <p>Nenhuma conta a receber encontrada</p>
              </div>
            )}
          </div>
        )}

        {abaAtiva === 'fluxo' && (
          <FluxoCaixaCompleto
            fluxoCaixa={fluxoCaixa}
            dataInicioFluxo={dataInicioFluxo}
            setDataInicioFluxo={setDataInicioFluxo}
            dataFimFluxo={dataFimFluxo}
            setDataFimFluxo={setDataFimFluxo}
            filtroOrigemFluxo={filtroOrigemFluxo}
            setFiltroOrigemFluxo={setFiltroOrigemFluxo}
          />
        )}

        {abaAtiva === 'lancamentos' && (
          <LancamentosCompleto
            lancamentos={lancamentosData?.lancamentos || []}
            estabelecimentoId={estabelecimentoId}
            refetchLancamentos={refetchLancamentos}
            modalLancamento={modalLancamento}
            setModalLancamento={setModalLancamento}
          />
        )}
      </div>

      {/* Modais */}
      {modalAberto && (
        <ModalNovaConta 
          isOpen={modalAberto} 
          onClose={() => setModalAberto(false)}
          estabelecimentoId={estabelecimentoId}
          fornecedores={fornecedores?.fornecedores || []}
        />
      )}

      {modalFornecedor && (
        <ModalFornecedores
          isOpen={modalFornecedor}
          onClose={() => setModalFornecedor(false)}
          estabelecimentoId={estabelecimentoId}
        />
      )}

      {/* Modal de Detalhes Vendas/Valor Líquido */}
      {modalDetalhes && (
        <ModalDetalhesFinanceiro
          isOpen={!!modalDetalhes}
          onClose={() => setModalDetalhes(null)}
          tipo={modalDetalhes}
          resumoMensal={resumoMensal}
        />
      )}
    </div>
  );
};

// Componente Card de Conta Premium
const ContaCard = ({ conta, tipo }) => {
  const [pagarModal, setPagarModal] = useState(false);
  const [pagamentoData, setPagamentoData] = useState({
    valorPago: (conta.valor - (conta.valorPago || 0)).toFixed(2),
    formaPagamento: 'pix',
    dataPagamento: new Date().toISOString().split('T')[0]
  });
  const queryClient = useQueryClient();

  const pagarMutation = useMutation(
    (data) => api.patch(`/api/financeiro/contas/${conta._id}/pagar`, data),
    {
      onSuccess: () => {
        toast.success('✅ Pagamento registrado com sucesso!');
        setPagarModal(false);
        // ✅ CRÍTICO: Invalidar TODOS os caches relacionados
        queryClient.invalidateQueries('contas-financeiro');
        queryClient.invalidateQueries('diaria-hoje');
        queryClient.invalidateQueries('fluxo-caixa'); // ✅ IMPORTANTE: Atualizar fluxo de caixa
        queryClient.invalidateQueries('resumo-mensal'); // ✅ Atualizar resumo mensal
        queryClient.invalidateQueries('caixa-atual'); // ✅ Atualizar caixa atual
        setTimeout(() => {
          queryClient.invalidateQueries('contas-financeiro');
          queryClient.invalidateQueries('fluxo-caixa'); // ✅ Re-invalidar após 1 segundo
        }, 1000);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao registrar pagamento');
      }
    }
  );

  const valorRestante = conta.valor - (conta.valorPago || 0);
  const isPago = conta.status === 'pago';
  const isVencido = new Date(conta.dataVencimento) < new Date() && !isPago;
  const dataCriacao = conta?.pedido?.createdAt || conta?.vendaFiado?.createdAt || conta?.createdAt;
  const idReferencia = conta?.pedido?.numero
    ? `Pedido #${conta.pedido.numero}`
    : conta?.vendaFiado?.numero
      ? `Venda #${conta.vendaFiado.numero}`
      : null;

  const itensFonte = Array.isArray(conta?.pedido?.itens)
    ? conta.pedido.itens
    : Array.isArray(conta?.vendaFiado?.itens)
      ? conta.vendaFiado.itens
      : [];

  const itensResumo = itensFonte
    .map((i) => {
      const nome = (i?.produto && typeof i.produto === 'object') ? i.produto.nome : undefined;
      const qtd = Number(i?.quantidade || 0);
      if (!nome || !qtd) return null;
      return `${qtd}x ${nome}`;
    })
    .filter(Boolean)
    .slice(0, 4)
    .join(', ');

  return (
    <>
      <div 
        className={`conta-card premium ${isPago ? 'pago' : ''} ${isVencido ? 'vencido' : ''}`}
        onClick={() => !isPago && setPagarModal(true)}
      >
        <div className="conta-header">
          <div className="conta-titulo">
            <h4>{conta.descricao}</h4>
            <span className={`categoria-badge ${conta.categoria === 'Entregadores' ? 'entregadores' : ''}`}>
              {conta.categoria === 'Entregadores' && '🚴 '}
              {conta.categoria}
            </span>
          </div>
          <div className={`status-badge premium ${conta.status}`}>
            {conta.status === 'pago' ? (
              <><CheckCircle2 size={16} /> Pago</>
            ) : conta.status === 'parcial' ? (
              <><Clock size={16} /> Parcial</>
            ) : isVencido ? (
              <><AlertCircle size={16} /> Vencido</>
            ) : (
              <><Clock size={16} /> Pendente</>
            )}
          </div>
        </div>

        <div className="conta-info">
          {idReferencia && (
            <div className="info-item">
              <FileText size={16} />
              <div>
                <span>Referência</span>
                <strong>{idReferencia}</strong>
              </div>
            </div>
          )}
          {dataCriacao && (
            <div className="info-item">
              <Clock size={16} />
              <div>
                <span>Data/Hora</span>
                <strong>{new Date(dataCriacao).toLocaleString('pt-BR')}</strong>
              </div>
            </div>
          )}
          {itensResumo && conta?.tipo === 'receber' && (
            <div className="info-item">
              <ShoppingBag size={16} />
              <div>
                <span>Itens</span>
                <strong>{itensResumo}</strong>
              </div>
            </div>
          )}
          <div className="info-item">
            <Calendar size={16} />
            <div>
              <span>Vencimento</span>
              <strong>{new Date(conta.dataVencimento).toLocaleDateString('pt-BR')}</strong>
            </div>
          </div>
          <div className="info-item">
            <DollarSign size={16} />
            <div>
              <span>Valor Total</span>
              <strong>R$ {conta.valor.toFixed(2)}</strong>
            </div>
          </div>
          {conta.valorPago > 0 && (
            <div className="info-item">
              <CheckCircle2 size={16} />
              <div>
                <span>Já Pago</span>
                <strong style={{ color: '#10b981' }}>R$ {conta.valorPago.toFixed(2)}</strong>
              </div>
            </div>
          )}
          {valorRestante > 0 && (
            <div className="info-item">
              <AlertCircle size={16} />
              <div>
                <span>Restante</span>
                <strong style={{ color: '#f59e0b' }}>R$ {valorRestante.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </div>

        {conta.fornecedor && (
          <div className="conta-fornecedor">
            <Building2 size={14} />
            <span>{conta.fornecedor}</span>
          </div>
        )}

        {conta.observacoes && (
          <div className="conta-observacoes">
            {conta.observacoes}
          </div>
        )}

        {!isPago && (
          <button 
            className="btn-pagar-premium"
            onClick={(e) => {
              e.stopPropagation();
              setPagarModal(true);
            }}
          >
            <CreditCard size={18} />
            Pagar R$ {valorRestante.toFixed(2)}
          </button>
        )}
      </div>

      {/* Modal de Pagamento Premium */}
      {pagarModal && (
        <ModalPagamentoPremium
          isOpen={pagarModal}
          onClose={() => setPagarModal(false)}
          conta={conta}
          pagamentoData={pagamentoData}
          setPagamentoData={setPagamentoData}
          pagarMutation={pagarMutation}
        />
      )}
    </>
  );
};

// Modal de Pagamento Premium
const ModalPagamentoPremium = ({ isOpen, onClose, conta, pagamentoData, setPagamentoData, pagarMutation }) => {
  if (!isOpen) return null;

  const valorRestante = conta.valor - (conta.valorPago || 0);
  const dataCriacao = conta?.pedido?.createdAt || conta?.vendaFiado?.createdAt || conta?.createdAt;
  const idReferencia = conta?.pedido?.numero
    ? `Pedido #${conta.pedido.numero}`
    : conta?.vendaFiado?.numero
      ? `Venda #${conta.vendaFiado.numero}`
      : null;

  const itensFonte = Array.isArray(conta?.pedido?.itens)
    ? conta.pedido.itens
    : Array.isArray(conta?.vendaFiado?.itens)
      ? conta.vendaFiado.itens
      : [];

  const itensResumo = itensFonte
    .map((i) => {
      const nome = (i?.produto && typeof i.produto === 'object') ? i.produto.nome : undefined;
      const qtd = Number(i?.quantidade || 0);
      if (!nome || !qtd) return null;
      return `${qtd}x ${nome}`;
    })
    .filter(Boolean)
    .slice(0, 6)
    .join(', ');

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>💳 Registrar Pagamento</h3>
            <p className="modal-subtitle">{conta.descricao}</p>
          </div>
          <button className="btn-close-premium" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body-premium">
          <div className="info-box-premium">
            {idReferencia && (
              <div className="info-row">
                <span>Referência:</span>
                <strong>{idReferencia}</strong>
              </div>
            )}
            {dataCriacao && (
              <div className="info-row">
                <span>Data/Hora:</span>
                <strong>{new Date(dataCriacao).toLocaleString('pt-BR')}</strong>
              </div>
            )}
            {itensResumo && (
              <div className="info-row">
                <span>Itens:</span>
                <strong>{itensResumo}</strong>
              </div>
            )}
            <div className="info-row">
              <span>Categoria:</span>
              <strong>{conta.categoria}</strong>
            </div>
            {conta.fornecedor && (
              <div className="info-row">
                <span>Fornecedor:</span>
                <strong>{conta.fornecedor}</strong>
              </div>
            )}
            <div className="info-row">
              <span>Valor Total:</span>
              <strong>R$ {conta.valor.toFixed(2)}</strong>
            </div>
            {conta.valorPago > 0 && (
              <div className="info-row">
                <span>Já Pago:</span>
                <strong style={{ color: '#10b981' }}>R$ {conta.valorPago.toFixed(2)}</strong>
              </div>
            )}
            <div className="info-row highlight">
              <span>Valor a Pagar:</span>
              <strong style={{ fontSize: '1.5rem', color: '#FF6B35' }}>
                R$ {valorRestante.toFixed(2)}
              </strong>
            </div>
          </div>

          <div className="form-group-premium">
            <label>
              <DollarSign size={18} />
              Valor a Pagar (R$) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={valorRestante}
              value={pagamentoData.valorPago}
              onChange={(e) => setPagamentoData({...pagamentoData, valorPago: e.target.value})}
              required
            />
          </div>

          <div className="form-group-premium">
            <label>
              <CreditCard size={18} />
              Forma de Pagamento *
            </label>
            <select
              value={pagamentoData.formaPagamento}
              onChange={(e) => setPagamentoData({...pagamentoData, formaPagamento: e.target.value})}
              required
              className="select-premium"
            >
              <option value="pix">💳 PIX</option>
              <option value="dinheiro">💵 Dinheiro</option>
              <option value="transferencia">🏦 Transferência Bancária</option>
              <option value="cartao_credito">💳 Cartão de Crédito</option>
              <option value="cartao_debito">💳 Cartão de Débito</option>
            </select>
          </div>

          <div className="form-group-premium">
            <label>
              <Calendar size={18} />
              Data do Pagamento *
            </label>
            <input
              type="date"
              value={pagamentoData.dataPagamento}
              onChange={(e) => setPagamentoData({...pagamentoData, dataPagamento: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="modal-footer-premium">
          <button className="btn-cancel-premium" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-confirm-premium"
            onClick={() => {
              pagarMutation.mutate({
                valorPago: parseFloat(pagamentoData.valorPago),
                dataPagamento: pagamentoData.dataPagamento,
                formaPagamento: pagamentoData.formaPagamento
              });
            }}
            disabled={pagarMutation.isLoading || !pagamentoData.valorPago || parseFloat(pagamentoData.valorPago) <= 0}
          >
            {pagarMutation.isLoading ? (
              <>⏳ Processando...</>
            ) : (
              <>✅ Confirmar Pagamento</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Nova Conta Premium
const ModalNovaConta = ({ isOpen, onClose, estabelecimentoId, fornecedores }) => {
  const [formData, setFormData] = useState({
    tipo: 'pagar',
    descricao: '',
    valor: '',
    dataVencimento: new Date().toISOString().split('T')[0],
    categoria: '',
    fornecedor: '',
    cliente: '',
    observacoes: ''
  });
  const queryClient = useQueryClient();

  const criarContaMutation = useMutation(
    (data) => api.post('/api/financeiro/contas', data),
    {
      onSuccess: () => {
        toast.success('✅ Conta criada com sucesso!');
        onClose();
        queryClient.invalidateQueries('contas-financeiro');
        setFormData({
          tipo: 'pagar',
          descricao: '',
          valor: '',
          dataVencimento: new Date().toISOString().split('T')[0],
          categoria: '',
          fornecedor: '',
          cliente: '',
          observacoes: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao criar conta');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!estabelecimentoId) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    criarContaMutation.mutate({
      ...formData,
      estabelecimento: estabelecimentoId,
      valor: parseFloat(formData.valor)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>➕ Nova Conta</h3>
            <p className="modal-subtitle">Cadastre uma nova conta a pagar ou receber</p>
          </div>
          <button className="btn-close-premium" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body-premium">
          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>
                <CreditCard size={18} />
                Tipo *
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                required
                className="select-premium"
              >
                <option value="pagar">Conta a Pagar</option>
                <option value="receber">Conta a Receber</option>
              </select>
            </div>

            <div className="form-group-premium">
              <label>
                <FileText size={18} />
                Categoria *
              </label>
              <input
                type="text"
                value={formData.categoria}
                onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                required
                placeholder="Ex: Fornecedores, Entregadores, etc"
              />
            </div>
          </div>

          <div className="form-group-premium">
            <label>
              <FileText size={18} />
              Descrição *
            </label>
            <input
              type="text"
              value={formData.descricao}
              onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              required
              placeholder="Ex: Pagamento fornecedor"
            />
          </div>

          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>
                <DollarSign size={18} />
                Valor (R$) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.valor}
                onChange={(e) => setFormData({...formData, valor: e.target.value})}
                required
                placeholder="0.00"
              />
            </div>

            <div className="form-group-premium">
              <label>
                <Calendar size={18} />
                Data de Vencimento *
              </label>
              <input
                type="date"
                value={formData.dataVencimento}
                onChange={(e) => setFormData({...formData, dataVencimento: e.target.value})}
                required
              />
            </div>
          </div>

          {formData.tipo === 'pagar' ? (
            <div className="form-group-premium">
              <label>
                <Building2 size={18} />
                Fornecedor
              </label>
              <select
                value={formData.fornecedor}
                onChange={(e) => setFormData({...formData, fornecedor: e.target.value})}
                className="select-premium"
              >
                <option value="">Selecione um fornecedor</option>
                {fornecedores.map(f => (
                  <option key={f._id} value={f.nome}>{f.nome}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group-premium">
              <label>
                <Building2 size={18} />
                Cliente
              </label>
              <input
                type="text"
                value={formData.cliente}
                onChange={(e) => setFormData({...formData, cliente: e.target.value})}
                placeholder="Nome do cliente"
              />
            </div>
          )}

          <div className="form-group-premium">
            <label>
              <FileText size={18} />
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              placeholder="Informações adicionais..."
              rows="3"
            />
          </div>

          <div className="modal-footer-premium">
            <button type="button" className="btn-cancel-premium" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-confirm-premium"
              disabled={criarContaMutation.isLoading}
            >
              {criarContaMutation.isLoading ? '⏳ Criando...' : '✅ Criar Conta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal Fornecedores
const ModalFornecedores = ({ isOpen, onClose, estabelecimentoId }) => {
  const [modalNovoFornecedor, setModalNovoFornecedor] = useState(false);
  const queryClient = useQueryClient();

  const { data: fornecedores } = useQuery(
    ['fornecedores-modal', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return { fornecedores: [] };
      const response = await api.get(`/api/compras/fornecedores?estabelecimento=${estabelecimentoId}`);
      return response.data.data || { fornecedores: [] };
    },
    {
      enabled: !!estabelecimentoId && isOpen
    }
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>🏢 Fornecedores</h3>
            <p className="modal-subtitle">Gerencie seus fornecedores</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className="btn-confirm-premium"
              onClick={() => setModalNovoFornecedor(true)}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              <Plus size={16} /> Novo
            </button>
            <button className="btn-close-premium" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="modal-body-premium">
          {fornecedores?.fornecedores?.length > 0 ? (
            <div className="fornecedores-lista">
              {fornecedores.fornecedores.map(fornecedor => (
                <div key={fornecedor._id} className="fornecedor-card">
                  <div>
                    <h4>{fornecedor.nome}</h4>
                    {fornecedor.contato?.telefone && (
                      <p>📞 {fornecedor.contato.telefone}</p>
                    )}
                    {fornecedor.contato?.email && (
                      <p>✉️ {fornecedor.contato.email}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Building2 size={64} />
              <p>Nenhum fornecedor cadastrado</p>
            </div>
          )}
        </div>
      </div>

      {modalNovoFornecedor && (
        <ModalNovoFornecedor
          isOpen={modalNovoFornecedor}
          onClose={() => setModalNovoFornecedor(false)}
          estabelecimentoId={estabelecimentoId}
        />
      )}
    </div>
  );
};

// Modal Novo Fornecedor
const ModalNovoFornecedor = ({ isOpen, onClose, estabelecimentoId }) => {
  const [formData, setFormData] = useState({
    nome: '',
    razaoSocial: '',
    cnpj: '',
    cpf: '',
    contato: {
      telefone: '',
      celular: '',
      email: '',
      whatsapp: ''
    },
    endereco: {
      rua: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    },
    dadosBancarios: {
      banco: '',
      agencia: '',
      conta: '',
      tipoConta: 'corrente',
      pix: ''
    },
    prazoPagamento: 30,
    observacoes: ''
  });
  const queryClient = useQueryClient();

  const criarMutation = useMutation(
    (data) => api.post('/api/compras/fornecedores', data),
    {
      onSuccess: () => {
        toast.success('✅ Fornecedor criado com sucesso!');
        onClose();
        queryClient.invalidateQueries('fornecedores-modal');
        queryClient.invalidateQueries('fornecedores-financeiro');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao criar fornecedor');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    criarMutation.mutate({
      ...formData,
      estabelecimento: estabelecimentoId
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>➕ Novo Fornecedor</h3>
            <p className="modal-subtitle">Cadastre um novo fornecedor</p>
          </div>
          <button className="btn-close-premium" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body-premium">
          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>Nome *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
                placeholder="Nome do fornecedor"
              />
            </div>
            <div className="form-group-premium">
              <label>Razão Social</label>
              <input
                type="text"
                value={formData.razaoSocial}
                onChange={(e) => setFormData({...formData, razaoSocial: e.target.value})}
                placeholder="Razão social"
              />
            </div>
          </div>

          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>CNPJ</label>
              <input
                type="text"
                value={formData.cnpj}
                onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div className="form-group-premium">
              <label>CPF</label>
              <input
                type="text"
                value={formData.cpf}
                onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                placeholder="000.000.000-00"
              />
            </div>
          </div>

          <div className="form-group-premium">
            <label>📞 Telefone</label>
            <input
              type="text"
              value={formData.contato.telefone}
              onChange={(e) => setFormData({
                ...formData,
                contato: {...formData.contato, telefone: e.target.value}
              })}
              placeholder="(00) 0000-0000"
            />
          </div>

          <div className="form-group-premium">
            <label>✉️ Email</label>
            <input
              type="email"
              value={formData.contato.email}
              onChange={(e) => setFormData({
                ...formData,
                contato: {...formData.contato, email: e.target.value}
              })}
              placeholder="email@exemplo.com"
            />
          </div>

          <div className="form-group-premium">
            <label>Prazo de Pagamento (dias)</label>
            <input
              type="number"
              value={formData.prazoPagamento}
              onChange={(e) => setFormData({...formData, prazoPagamento: parseInt(e.target.value)})}
              min="0"
            />
          </div>

          <div className="form-group-premium">
            <label>Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              rows="3"
              placeholder="Informações adicionais..."
            />
          </div>

          <div className="modal-footer-premium">
            <button type="button" className="btn-cancel-premium" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-confirm-premium"
              disabled={criarMutation.isLoading}
            >
              {criarMutation.isLoading ? '⏳ Criando...' : '✅ Criar Fornecedor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Detalhes Financeiro
const ModalDetalhesFinanceiro = ({ isOpen, onClose, tipo, resumoMensal }) => {
  if (!isOpen || !resumoMensal) return null;

  const isEntradas = tipo === 'entradas';
  const isVendas = tipo === 'vendas'; // Mantido para compatibilidade
  const isLiquido = tipo === 'liquido';
  
  // Para compatibilidade, tratar 'vendas' como 'entradas'
  const mostrarEntradas = isEntradas || isVendas;

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>
              {isEntradas || isVendas ? '💰 Entradas Totais do Mês' : '💵 Valor Líquido do Mês'}
            </h3>
            <p className="modal-subtitle">
              {isEntradas || isVendas 
                ? `Período: ${new Date(resumoMensal.periodo.inicio).toLocaleDateString('pt-BR')} até ${new Date(resumoMensal.periodo.fim).toLocaleDateString('pt-BR')}`
                : 'Lucro líquido após todas as deduções e despesas'
              }
            </p>
          </div>
          <button className="btn-close-premium" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body-premium">
          {/* Resumo Geral */}
          <div className="info-box-premium" style={{ marginBottom: '1.5rem' }}>
            {isEntradas || isVendas ? (
              <>
                <div className="info-row highlight">
                  <span>Entradas Totais:</span>
                  <strong style={{ fontSize: '1.75rem', color: '#667eea' }}>
                    R$ {(resumoMensal.entradasTotais || resumoMensal.vendasTotais || 0).toFixed(2)}
                  </strong>
                </div>
                <div className="info-row">
                  <span>Vendas (Pedidos + Caixa):</span>
                  <strong>R$ {resumoMensal.vendasTotais.toFixed(2)}</strong>
                  <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                    {resumoMensal.resumo?.quantidadePedidos || 0} pedido(s) + {resumoMensal.resumo?.quantidadeVendas || 0} venda(s)
                  </small>
                </div>
                {resumoMensal.totalReceitas > 0 && (
                  <div className="info-row">
                    <span>Receitas (Lançamentos):</span>
                    <strong style={{ color: '#10b981' }}>+ R$ {resumoMensal.totalReceitas.toFixed(2)}</strong>
                    <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                      {resumoMensal.resumo?.quantidadeReceitas || 0} receita(s)
                    </small>
                  </div>
                )}
                <div className="info-row highlight" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span>Total de Entradas:</span>
                  <strong style={{ fontSize: '1.5rem', color: '#667eea' }}>
                    R$ {(resumoMensal.entradasTotais || resumoMensal.vendasTotais || 0).toFixed(2)}
                  </strong>
                </div>
              </>
            ) : (
              <>
                <div className="info-row highlight">
                  <span>Valor Líquido:</span>
                  <strong style={{ fontSize: '1.75rem', color: '#f5576c' }}>
                    R$ {resumoMensal.valorLiquido.toFixed(2)}
                  </strong>
                </div>
                <div className="info-row">
                  <span>Entradas Totais:</span>
                  <strong style={{ color: '#667eea' }}>R$ {(resumoMensal.entradasTotais || resumoMensal.vendasTotais || 0).toFixed(2)}</strong>
                  <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                    Vendas: R$ {resumoMensal.vendasTotais.toFixed(2)} + Receitas: R$ {(resumoMensal.totalReceitas || 0).toFixed(2)}
                  </small>
                </div>
                <div className="info-row">
                  <span>Descontos Aplicados:</span>
                  <strong style={{ color: '#ef4444' }}>- R$ {resumoMensal.totalDescontos.toFixed(2)}</strong>
                </div>
                <div className="info-row">
                  <span>Contas Pagas:</span>
                  <strong style={{ color: '#ef4444' }}>- R$ {resumoMensal.totalContasPagas.toFixed(2)}</strong>
                  <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                    {resumoMensal.resumo?.quantidadeContasPagas || 0} conta(s) paga(s)
                  </small>
                </div>
                {resumoMensal.totalDespesas > 0 && (
                  <div className="info-row">
                    <span>Despesas (Lançamentos):</span>
                    <strong style={{ color: '#ef4444' }}>- R$ {resumoMensal.totalDespesas.toFixed(2)}</strong>
                    <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                      {resumoMensal.resumo?.quantidadeDespesas || 0} despesa(s)
                    </small>
                  </div>
                )}
                <div className="info-row highlight" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span>Valor Líquido Final:</span>
                  <strong style={{ fontSize: '1.5rem', color: '#f5576c' }}>
                    R$ {resumoMensal.valorLiquido.toFixed(2)}
                  </strong>
                  <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem', fontStyle: 'italic' }}>
                    Entradas - Descontos - Contas Pagas - Despesas
                  </small>
                </div>
              </>
            )}
          </div>

          {/* Vendas por Tipo */}
          {mostrarEntradas && resumoMensal.resumo?.vendasPorTipo && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#fff' }}>Vendas por Tipo</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                {Object.entries(resumoMensal.resumo.vendasPorTipo).map(([tipo, dados]) => (
                  <div key={tipo} className="info-box-premium" style={{ padding: '1rem' }}>
                    <div style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {tipo === 'balcao' ? '🛒 Balcão' : tipo === 'delivery' ? '🚴 Delivery' : tipo === 'chat' ? '💬 Chat' : '📦 Retirada'}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                      {dados.quantidade} vendas
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#667eea', marginTop: '0.25rem' }}>
                      R$ {dados.total.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contas por Categoria */}
          {isLiquido && resumoMensal.resumo?.contasPorCategoria && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#fff' }}>Contas Pagas por Categoria</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                {Object.entries(resumoMensal.resumo.contasPorCategoria).map(([categoria, dados]) => (
                  <div key={categoria} className="info-box-premium" style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {categoria}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                      {dados.quantidade} contas
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginTop: '0.25rem' }}>
                      R$ {dados.total.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lista Detalhada */}
          <div>
            <h4 style={{ marginBottom: '1rem', color: '#fff' }}>
              {(isEntradas || isVendas) ? '📋 Detalhamento de Entradas (Vendas + Receitas)' : '📋 Detalhamento de Saídas (Contas Pagas + Despesas)'}
            </h4>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {mostrarEntradas ? (
                ((resumoMensal.detalhes?.vendas && resumoMensal.detalhes.vendas.length > 0) || 
                 (resumoMensal.detalhes?.receitas && resumoMensal.detalhes.receitas.length > 0)) ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Data</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Tipo</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Descrição</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Categoria/Cliente</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#fff' }}>Valor</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoMensal.detalhes.vendas?.map((venda, idx) => (
                        <tr key={`venda-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            {new Date(venda.data).toLocaleDateString('pt-BR')}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            <span style={{ background: '#667eea', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', color: 'white' }}>
                              Venda #{venda.numero}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            {venda.tipoVenda === 'balcao' ? '🛒 Balcão' : venda.tipoVenda === 'delivery' ? '🚴 Delivery' : venda.tipoVenda === 'chat' ? '💬 Chat' : '📦 Retirada'}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{venda.cliente}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#667eea' }}>
                            R$ {(venda.total ?? venda.subtotal ?? 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc', textTransform: 'capitalize' }}>
                            {venda.formaPagamento}
                          </td>
                        </tr>
                      ))}
                      {resumoMensal.detalhes.receitas?.map((receita, idx) => (
                        <tr key={`receita-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            {new Date(receita.data).toLocaleDateString('pt-BR')}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            <span style={{ background: '#10b981', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', color: 'white' }}>
                              💰 Receita
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{receita.descricao}</td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{receita.categoria}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                            + R$ {receita.valor.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc', textTransform: 'capitalize' }}>
                            {receita.formaPagamento}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <FileText size={48} />
                    <p>Nenhuma venda encontrada</p>
                  </div>
                )
              ) : (
                ((resumoMensal.detalhes?.contas && resumoMensal.detalhes.contas.length > 0) || 
                 (resumoMensal.detalhes?.despesas && resumoMensal.detalhes.despesas.length > 0)) ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Data</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Tipo</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Descrição</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Categoria</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#fff' }}>Valor</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#fff' }}>Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoMensal.detalhes.contas?.map((conta, idx) => (
                        <tr key={`conta-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            {new Date(conta.dataPagamento).toLocaleDateString('pt-BR')}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            <span style={{ background: '#ef4444', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', color: 'white' }}>
                              💳 Conta Paga
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{conta.descricao}</td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{conta.categoria}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#ef4444' }}>
                            - R$ {conta.valor.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc', textTransform: 'capitalize' }}>
                            {conta.formaPagamento}
                          </td>
                        </tr>
                      ))}
                      {resumoMensal.detalhes.despesas?.map((despesa, idx) => (
                        <tr key={`despesa-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            {new Date(despesa.data).toLocaleDateString('pt-BR')}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>
                            <span style={{ 
                              background: '#ef4444', 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '0.25rem', 
                              fontSize: '0.75rem',
                              color: 'white'
                            }}>
                              💸 Despesa
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{despesa.descricao}</td>
                          <td style={{ padding: '0.75rem', color: '#ccc' }}>{despesa.categoria}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#ef4444' }}>
                            - R$ {despesa.valor.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.75rem', color: '#ccc', textTransform: 'capitalize' }}>
                            {despesa.formaPagamento}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <FileText size={48} />
                    <p>Nenhuma saída encontrada</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer-premium">
          <button className="btn-confirm-premium" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Relatórios Completo
const RelatoriosCompleto = ({
  tipoRelatorio, setTipoRelatorio,
  agrupamento, setAgrupamento,
  dataInicio, setDataInicio,
  dataFim, setDataFim,
  buscaRelatorio, setBuscaRelatorio,
  filtroStatusRelatorio, setFiltroStatusRelatorio,
  filtroPagamentoRelatorio, setFiltroPagamentoRelatorio,
  limiteProdutos, setLimiteProdutos,
  visualizacao, setVisualizacao,
  relatorioVendas, loadingVendas,
  relatorioProdutos, loadingProdutos,
  relatorioPedidos, loadingPedidos,
  estabelecimentoId,
  contas
}) => {
  return (
    <div className="relatorios-page-premium">
      {/* Header do Relatório */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', color: 'white' }}>
        <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.75rem' }}>📊 Relatórios Financeiros</h2>
        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>Análises detalhadas e insights do seu negócio</p>
      </div>

      {/* Filtros Organizados */}
      <div className="filtros-container-premium" style={{ marginBottom: '2rem', background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Tipo de Relatório */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
            <FileText size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Tipo de Relatório
          </label>
          <select
            value={tipoRelatorio}
            onChange={(e) => setTipoRelatorio(e.target.value)}
            className="select-premium-large"
            style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
          >
            <option value="vendas">📈 Vendas por Período</option>
            <option value="produtos">📦 Produtos Mais Vendidos</option>
            <option value="pedidos">🛒 Pedidos Detalhados</option>
            <option value="financeiro">💰 Financeiro Completo</option>
            <option value="entregadores">🚴 Por Entregador</option>
          </select>
        </div>

        {/* Período */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
              <Calendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Data Inicial
            </label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="input-premium"
              style={{ width: '100%', padding: '0.75rem' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
              <Calendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Data Final
            </label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="input-premium"
              style={{ width: '100%', padding: '0.75rem' }}
            />
          </div>
        </div>

        {/* Filtros Específicos por Tipo */}
        {tipoRelatorio === 'vendas' && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
              <BarChart3 size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Agrupar por
            </label>
            <select
              value={agrupamento}
              onChange={(e) => setAgrupamento(e.target.value)}
              className="select-premium"
              style={{ width: '100%', padding: '0.75rem' }}
            >
              <option value="dia">📅 Por Dia</option>
              <option value="semana">📆 Por Semana</option>
              <option value="mes">📊 Por Mês</option>
            </select>
          </div>
        )}

        {tipoRelatorio === 'produtos' && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
              <Package size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Quantidade de Produtos
            </label>
            <select
              value={limiteProdutos}
              onChange={(e) => setLimiteProdutos(parseInt(e.target.value))}
              className="select-premium"
              style={{ width: '100%', padding: '0.75rem' }}
            >
              <option value="10">Top 10 Produtos</option>
              <option value="20">Top 20 Produtos</option>
              <option value="50">Top 50 Produtos</option>
              <option value="100">Top 100 Produtos</option>
            </select>
          </div>
        )}

        {tipoRelatorio === 'pedidos' && (
          <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                <Filter size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Status do Pedido
              </label>
              <select
                value={filtroStatusRelatorio}
                onChange={(e) => setFiltroStatusRelatorio(e.target.value)}
                className="select-premium"
                style={{ width: '100%', padding: '0.75rem' }}
              >
                <option value="todos">✅ Todos os Status</option>
                <option value="Pendente">⏳ Pendente</option>
                <option value="Confirmado">✓ Confirmado</option>
                <option value="Em preparo">👨‍🍳 Em preparo</option>
                <option value="Pronto">✅ Pronto</option>
                <option value="Saiu para entrega">🚴 Saiu para entrega</option>
                <option value="Entregue">📦 Entregue</option>
                <option value="Cancelado">❌ Cancelado</option>
              </select>
            </div>

            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                <CreditCard size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Status de Pagamento
              </label>
              <select
                value={filtroPagamentoRelatorio}
                onChange={(e) => setFiltroPagamentoRelatorio(e.target.value)}
                className="select-premium"
                style={{ width: '100%', padding: '0.75rem' }}
              >
                <option value="todos">💰 Todos os Pagamentos</option>
                <option value="Pendente">⏳ Pendente</option>
                <option value="Pago">✅ Pago</option>
                <option value="Cancelado">❌ Cancelado</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                <Search size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Buscar Pedido
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Buscar por número, cliente, telefone..."
                  value={buscaRelatorio}
                  onChange={(e) => setBuscaRelatorio(e.target.value)}
                  className="input-busca-premium"
                  style={{ width: '100%', padding: '0.75rem', paddingRight: buscaRelatorio ? '2.5rem' : '0.75rem' }}
                />
                {buscaRelatorio && (
                  <button 
                    onClick={() => setBuscaRelatorio('')}
                    className="btn-clear"
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conteúdo dos Relatórios */}
      <div className="relatorio-content-premium">
        {tipoRelatorio === 'vendas' && (
          <RelatorioVendas 
            data={relatorioVendas} 
            loading={loadingVendas}
            agrupamento={agrupamento}
            visualizacao={visualizacao}
            setVisualizacao={setVisualizacao}
          />
        )}

        {tipoRelatorio === 'produtos' && (
          <RelatorioProdutos 
            data={relatorioProdutos} 
            loading={loadingProdutos}
            visualizacao={visualizacao}
            setVisualizacao={setVisualizacao}
          />
        )}

        {tipoRelatorio === 'pedidos' && (
          <RelatorioPedidos 
            data={relatorioPedidos} 
            loading={loadingPedidos}
            visualizacao={visualizacao}
            setVisualizacao={setVisualizacao}
          />
        )}

        {tipoRelatorio === 'financeiro' && (
          <RelatorioFinanceiroDetalhado estabelecimentoId={estabelecimentoId} />
        )}

        {tipoRelatorio === 'entregadores' && (
          <RelatorioEntregadores estabelecimentoId={estabelecimentoId} />
        )}
      </div>
    </div>
  );
};

// Componente Relatório de Vendas Premium
const RelatorioVendas = ({ data, loading, agrupamento, visualizacao, setVisualizacao }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p style={{ color: '#666' }}>Carregando relatório...</p>
      </div>
    );
  }

  if (!data || !data.vendas || data.vendas.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
        <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Nenhuma venda encontrada</h3>
        <p style={{ color: '#666' }}>Não há vendas no período selecionado</p>
      </div>
    );
  }

  const total = data.total || { faturamento: 0, quantidade: 0 };
  const periodoLabel = {
    dia: 'Dia',
    semana: 'Semana',
    mes: 'Mês'
  }[agrupamento] || 'Período';

  const parsePeriodoDate = (periodo) => {
    if (!periodo) return null;
    if (periodo instanceof Date) return periodo;
    if (typeof periodo === 'string') {
      const match = periodo.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const ano = Number(match[1]);
        const mes = Number(match[2]) - 1;
        const dia = Number(match[3]);
        return new Date(ano, mes, dia);
      }
    }
    return new Date(periodo);
  };

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      {/* Header do Relatório */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #f0f0f0' }}>
        <div>
          <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.5rem', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={24} color="#667eea" />
            Vendas por {periodoLabel}
          </h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            📅 {parsePeriodoDate(data.periodo?.inicio)?.toLocaleDateString('pt-BR') || ''} até {parsePeriodoDate(data.periodo?.fim)?.toLocaleDateString('pt-BR') || ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', background: '#f8f9fa', padding: '0.25rem', borderRadius: '8px' }}>
          <button
            className={`toggle-btn ${visualizacao === 'card' ? 'active' : ''}`}
            onClick={() => setVisualizacao('card')}
            title="Visualização em Cards"
            style={{ padding: '0.5rem', border: 'none', background: visualizacao === 'card' ? '#667eea' : 'transparent', color: visualizacao === 'card' ? 'white' : '#666', borderRadius: '6px', cursor: 'pointer' }}
          >
            <LayoutGrid size={18} />
          </button>
          <button
            className={`toggle-btn ${visualizacao === 'lista' ? 'active' : ''}`}
            onClick={() => setVisualizacao('lista')}
            title="Visualização em Lista"
            style={{ padding: '0.5rem', border: 'none', background: visualizacao === 'lista' ? '#667eea' : 'transparent', color: visualizacao === 'lista' ? 'white' : '#666', borderRadius: '6px', cursor: 'pointer' }}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Cards de Resumo - Reorganizados */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <ShoppingCart size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total de Vendas</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{total.quantidade}</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <DollarSign size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Faturamento Total</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>R$ {total.faturamento.toFixed(2)}</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <TrendingUp size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Ticket Médio</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>
                R$ {total.quantidade > 0 ? (total.faturamento / total.quantidade).toFixed(2) : '0.00'}
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <Calendar size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Períodos Analisados</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{data.vendas.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela Detalhada ou Cards */}
      <div style={{ marginTop: '2rem' }}>
        <h4 style={{ marginBottom: '1rem', color: '#333', fontSize: '1.1rem', fontWeight: 'bold' }}>
          📋 Detalhamento por {periodoLabel}
        </h4>
        
        {visualizacao === 'lista' ? (
          <div style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>{periodoLabel}</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Quantidade</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>Faturamento</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>Ticket Médio</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>% do Total</th>
                </tr>
              </thead>
          <tbody>
            {data.vendas.map((venda, idx) => {
              const percentual = total.faturamento > 0 
                ? ((venda.faturamento / total.faturamento) * 100).toFixed(1)
                : 0;
              const ticketMedio = venda.quantidade > 0 
                ? (venda.faturamento / venda.quantidade).toFixed(2)
                : '0.00';
              
              let periodoFormatado = venda.periodo;
              if (agrupamento === 'dia') {
                periodoFormatado = parsePeriodoDate(venda.periodo)?.toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) || venda.periodo;
              } else if (agrupamento === 'semana') {
                periodoFormatado = `Semana ${venda.periodo.split('-W')[1]} de ${venda.periodo.split('-W')[0]}`;
              } else if (agrupamento === 'mes') {
                const [ano, mes] = venda.periodo.split('-');
                periodoFormatado = new Date(parseInt(ano), parseInt(mes) - 1).toLocaleDateString('pt-BR', {
                  year: 'numeric',
                  month: 'long'
                });
              }

              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' }} 
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '1rem', color: '#333', fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Calendar size={16} color="#667eea" />
                      <span>{periodoFormatado}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <ShoppingCart size={16} color="#10b981" />
                      <strong style={{ fontSize: '1rem' }}>{venda.quantidade}</strong>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', color: '#333', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <DollarSign size={16} color="#10b981" />
                      <span style={{ fontSize: '1rem', color: '#10b981' }}>R$ {venda.faturamento.toFixed(2)}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', color: '#666' }}>
                    <span style={{ fontSize: '0.95rem' }}>R$ {ticketMedio}</span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                      <div style={{ width: '100px', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div 
                          style={{ 
                            width: `${percentual}%`, 
                            height: '100%', 
                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            transition: 'width 0.3s'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: '500' }}>{percentual}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {data.vendas.map((venda, idx) => {
            const percentual = total.faturamento > 0 
              ? ((venda.faturamento / total.faturamento) * 100).toFixed(1)
              : 0;
            const ticketMedio = venda.quantidade > 0 
              ? (venda.faturamento / venda.quantidade).toFixed(2)
              : '0.00';
            
            let periodoFormatado = venda.periodo;
            if (agrupamento === 'dia') {
              periodoFormatado = parsePeriodoDate(venda.periodo)?.toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) || venda.periodo;
            } else if (agrupamento === 'semana') {
              periodoFormatado = `Semana ${venda.periodo.split('-W')[1]} de ${venda.periodo.split('-W')[0]}`;
            } else if (agrupamento === 'mes') {
              const [ano, mes] = venda.periodo.split('-');
              periodoFormatado = new Date(parseInt(ano), parseInt(mes) - 1).toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long'
              });
            }

            return (
              <div key={idx} style={{ 
                background: '#fff', 
                borderRadius: '12px', 
                padding: '1.5rem', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '2px solid #f0f0f0' }}>
                  <Calendar size={24} color="#667eea" />
                  <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#333', fontWeight: 'bold' }}>{periodoFormatado}</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <ShoppingCart size={20} color="#10b981" style={{ marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Vendas</div>
                    <strong style={{ fontSize: '1.25rem', color: '#333' }}>{venda.quantidade}</strong>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <DollarSign size={20} color="#10b981" style={{ marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Faturamento</div>
                    <strong style={{ fontSize: '1.25rem', color: '#10b981' }}>R$ {venda.faturamento.toFixed(2)}</strong>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <TrendingUp size={20} color="#3b82f6" style={{ marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Ticket Médio</div>
                    <strong style={{ fontSize: '1.25rem', color: '#333' }}>R$ {ticketMedio}</strong>
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>{percentual}% do total</span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div 
                      style={{ 
                        width: `${percentual}%`, 
                        height: '100%', 
                        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                        transition: 'width 0.3s'
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
};

// Componente Relatório de Produtos Premium
const RelatorioProdutos = ({ data, loading, visualizacao, setVisualizacao }) => {
  if (loading) {
    return <LoadingState />;
  }

  if (!data || !data.produtos || data.produtos.length === 0) {
    return <EmptyState message="Nenhum produto vendido no período selecionado" />;
  }

  const totalFaturamento = data.produtos.reduce((acc, p) => acc + p.faturamento, 0);
  const totalQuantidade = data.produtos.reduce((acc, p) => acc + p.quantidade, 0);

  return (
    <div className="relatorio-card-premium">
      <div className="card-header-premium">
        <div>
          <h3>
            <Package size={24} />
            Produtos Mais Vendidos
          </h3>
          <p className="card-subtitle">
            {data.produtos.length} produtos encontrados
          </p>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${visualizacao === 'card' ? 'active' : ''}`}
            onClick={() => setVisualizacao('card')}
            title="Visualização em Cards"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            className={`toggle-btn ${visualizacao === 'lista' ? 'active' : ''}`}
            onClick={() => setVisualizacao('lista')}
            title="Visualização em Lista"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Resumo */}
      <div className="stats-grid-premium">
        <div className="stat-card-premium primary">
          <div className="stat-icon-premium">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <span>Total de Unidades</span>
            <strong>{totalQuantidade}</strong>
          </div>
        </div>
        <div className="stat-card-premium success">
          <div className="stat-icon-premium">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span>Faturamento Total</span>
            <strong>R$ {totalFaturamento.toFixed(2)}</strong>
          </div>
        </div>
        <div className="stat-card-premium info">
          <div className="stat-icon-premium">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span>Preço Médio</span>
            <strong>R$ {totalQuantidade > 0 ? (totalFaturamento / totalQuantidade).toFixed(2) : '0.00'}</strong>
          </div>
        </div>
      </div>

      {/* Lista de Produtos - Cards ou Lista */}
      {visualizacao === 'lista' ? (
        <div className="produtos-lista-premium">
          <table className="tabela-premium">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Rank</th>
                <th>Produto</th>
                <th>Categoria</th>
                <th>Quantidade</th>
                <th>Faturamento</th>
                <th>Preço Médio</th>
                <th>% Faturamento</th>
              </tr>
            </thead>
            <tbody>
              {data.produtos.map((produto, idx) => {
                const percentualFaturamento = totalFaturamento > 0 
                  ? ((produto.faturamento / totalFaturamento) * 100).toFixed(1)
                  : 0;
                const precoMedio = produto.quantidade > 0
                  ? (produto.faturamento / produto.quantidade).toFixed(2)
                  : '0.00';

                return (
                  <tr key={produto.id || idx}>
                    <td>
                      <div className={`rank-badge-table rank-${idx + 1}`}>
                        {idx === 0 && '🥇'}
                        {idx === 1 && '🥈'}
                        {idx === 2 && '🥉'}
                        {![0, 1, 2].includes(idx) && `#${idx + 1}`}
                      </div>
                    </td>
                    <td>
                      <strong>{produto.nome}</strong>
                    </td>
                    <td>
                      <span className="categoria-tag">{produto.categoria}</span>
                    </td>
                    <td>
                      <strong>{produto.quantidade}</strong>
                    </td>
                    <td>
                      <strong style={{ color: '#10b981' }}>R$ {produto.faturamento.toFixed(2)}</strong>
                    </td>
                    <td>
                      <span>R$ {precoMedio}</span>
                    </td>
                    <td>
                      <div className="percentual-cell">
                        <div className="percentual-bar">
                          <div 
                            className="progress-fill success" 
                            style={{ width: `${percentualFaturamento}%` }}
                          />
                        </div>
                        <span>{percentualFaturamento}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="produtos-grid-premium">
          {data.produtos.map((produto, idx) => {
          const percentualFaturamento = totalFaturamento > 0 
            ? ((produto.faturamento / totalFaturamento) * 100).toFixed(1)
            : 0;
          const percentualQuantidade = totalQuantidade > 0
            ? ((produto.quantidade / totalQuantidade) * 100).toFixed(1)
            : 0;
          const precoMedio = produto.quantidade > 0
            ? (produto.faturamento / produto.quantidade).toFixed(2)
            : '0.00';

          return (
            <div key={produto.id || idx} className="produto-card-premium">
              <div className="produto-rank-premium">
                <div className={`rank-badge rank-${idx + 1}`}>
                  {idx === 0 && '🥇'}
                  {idx === 1 && '🥈'}
                  {idx === 2 && '🥉'}
                  {![0, 1, 2].includes(idx) && `#${idx + 1}`}
                </div>
                <div className="produto-info-premium">
                  <h4>{produto.nome}</h4>
                  <span className="categoria-tag">{produto.categoria}</span>
                </div>
              </div>

              <div className="produto-stats-premium">
                <div className="stat-item-premium">
                  <div className="stat-label">
                    <ShoppingCart size={16} />
                    <span>Quantidade</span>
                  </div>
                  <div className="stat-value">
                    <strong>{produto.quantidade}</strong>
                    <span className="percentual">{percentualQuantidade}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${percentualQuantidade}%` }}
                    />
                  </div>
                </div>

                <div className="stat-item-premium">
                  <div className="stat-label">
                    <DollarSign size={16} />
                    <span>Faturamento</span>
                  </div>
                  <div className="stat-value">
                    <strong>R$ {produto.faturamento.toFixed(2)}</strong>
                    <span className="percentual">{percentualFaturamento}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill success" 
                      style={{ width: `${percentualFaturamento}%` }}
                    />
                  </div>
                </div>

                <div className="stat-item-premium">
                  <div className="stat-label">
                    <TrendingUp size={16} />
                    <span>Preço Médio</span>
                  </div>
                  <div className="stat-value">
                    <strong>R$ {precoMedio}</strong>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

// Componente Relatório de Pedidos Detalhado
const RelatorioPedidos = ({ data, loading, visualizacao, setVisualizacao }) => {
  if (loading) {
    return <LoadingState />;
  }

  if (!data || !data.pedidos || data.pedidos.length === 0) {
    return <EmptyState message="Nenhum pedido encontrado com os filtros selecionados" />;
  }

  const total = data.pedidos.reduce((acc, p) => acc + (p.total || 0), 0);
  const pedidosPagos = data.pedidos.filter(p => p.statusPagamento === 'Pago').length;
  const pedidosPendentes = data.pedidos.filter(p => p.statusPagamento === 'Pendente').length;

  return (
    <div className="relatorio-card-premium">
      <div className="card-header-premium">
        <div>
          <h3>
            <ShoppingCart size={24} />
            Pedidos Detalhados
          </h3>
          <p className="card-subtitle">
            {data.pedidos.length} pedidos encontrados
          </p>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${visualizacao === 'card' ? 'active' : ''}`}
            onClick={() => setVisualizacao('card')}
            title="Visualização em Cards"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            className={`toggle-btn ${visualizacao === 'lista' ? 'active' : ''}`}
            onClick={() => setVisualizacao('lista')}
            title="Visualização em Lista"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Resumo */}
      <div className="stats-grid-premium">
        <div className="stat-card-premium primary">
          <div className="stat-icon-premium">
            <ShoppingCart size={24} />
          </div>
          <div className="stat-content">
            <span>Total de Pedidos</span>
            <strong>{data.pedidos.length}</strong>
          </div>
        </div>
        <div className="stat-card-premium success">
          <div className="stat-icon-premium">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span>Faturamento Total</span>
            <strong>R$ {total.toFixed(2)}</strong>
          </div>
        </div>
        <div className="stat-card-premium info">
          <div className="stat-icon-premium">
            <CheckCircle2 size={24} />
          </div>
          <div className="stat-content">
            <span>Pedidos Pagos</span>
            <strong>{pedidosPagos}</strong>
          </div>
        </div>
        <div className="stat-card-premium warning">
          <div className="stat-icon-premium">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span>Pedidos Pendentes</span>
            <strong>{pedidosPendentes}</strong>
          </div>
        </div>
      </div>

      {/* Tabela de Pedidos ou Cards */}
      {visualizacao === 'lista' ? (
        <div className="tabela-container-premium">
          <table className="tabela-premium">
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Cliente</th>
              <th>Data/Hora</th>
              <th>Status</th>
              <th>Pagamento</th>
              <th>Forma Pagamento</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {data.pedidos.map((pedido) => (
              <tr key={pedido._id}>
                <td>
                  <div className="pedido-number">
                    <strong>#{pedido.numero}</strong>
                  </div>
                </td>
                <td>
                  <div className="cliente-info">
                    <strong>{pedido.cliente?.nome || 'Cliente'}</strong>
                    {pedido.cliente?.telefone && (
                      <span>{pedido.cliente.telefone}</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="data-info">
                    <Calendar size={14} />
                    <span>{new Date(pedido.createdAt).toLocaleDateString('pt-BR')}</span>
                    <Clock size={14} />
                    <span>{new Date(pedido.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge-premium ${pedido.status?.toLowerCase().replace(' ', '-')}`}>
                    {pedido.status}
                  </span>
                </td>
                <td>
                  <span className={`status-badge-premium ${pedido.statusPagamento?.toLowerCase()}`}>
                    {pedido.statusPagamento === 'Pago' ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                    {pedido.statusPagamento}
                  </span>
                </td>
                <td>
                  <span className="forma-pagamento-tag">
                    {pedido.formaPagamento === 'pix' && '💳 PIX'}
                    {pedido.formaPagamento === 'dinheiro' && '💵 Dinheiro'}
                    {pedido.formaPagamento === 'cartao_credito' && '💳 Crédito'}
                    {pedido.formaPagamento === 'cartao_debito' && '💳 Débito'}
                    {!['pix', 'dinheiro', 'cartao_credito', 'cartao_debito'].includes(pedido.formaPagamento) && pedido.formaPagamento}
                  </span>
                </td>
                <td>
                  <strong className="valor-total">R$ {pedido.total?.toFixed(2)}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : (
        <div className="pedidos-cards-grid">
          {data.pedidos.map((pedido) => (
            <div key={pedido._id} className="pedido-card-item">
              <div className="pedido-card-header">
                <div>
                  <strong>Pedido #{pedido.numero}</strong>
                  <span>{new Date(pedido.createdAt).toLocaleDateString('pt-BR')}</span>
                </div>
                <span className={`status-badge-premium ${pedido.status?.toLowerCase().replace(' ', '-')}`}>
                  {pedido.status}
                </span>
              </div>
              <div className="pedido-card-body">
                <div className="pedido-info-item">
                  <Users size={16} />
                  <div>
                    <span>Cliente</span>
                    <strong>{pedido.cliente?.nome || 'Cliente'}</strong>
                    {pedido.cliente?.telefone && (
                      <span className="telefone">{pedido.cliente.telefone}</span>
                    )}
                  </div>
                </div>
                <div className="pedido-info-item">
                  <CreditCard size={16} />
                  <div>
                    <span>Pagamento</span>
                    <strong className={pedido.statusPagamento === 'Pago' ? 'pago' : 'pendente'}>
                      {pedido.statusPagamento}
                    </strong>
                    <span className="forma-pagamento">
                      {pedido.formaPagamento === 'pix' && '💳 PIX'}
                      {pedido.formaPagamento === 'dinheiro' && '💵 Dinheiro'}
                      {pedido.formaPagamento === 'cartao_credito' && '💳 Crédito'}
                      {pedido.formaPagamento === 'cartao_debito' && '💳 Débito'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="pedido-card-footer">
                <div className="pedido-valor">
                  <DollarSign size={20} />
                  <strong>R$ {pedido.total?.toFixed(2)}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente Relatório Financeiro Detalhado
const RelatorioFinanceiroDetalhado = ({ estabelecimentoId }) => {
  // ✅ Buscar TODAS as contas (sem filtro de status) para o relatório
  const { data: contasData, isLoading } = useQuery(
    ['contas-financeiro-relatorio', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) {
        return { contas: [] };
      }
      try {
        const params = new URLSearchParams();
        params.append('estabelecimento', estabelecimentoId);
        // ✅ NÃO filtrar por status - buscar TODAS as contas
        
        const response = await api.get(`/api/financeiro/contas?${params}`);
        console.log('📊 Relatório Financeiro - Resposta da API:', response.data);
        return response.data.data || { contas: [] };
      } catch (error) {
        console.error('❌ Erro ao buscar contas para relatório:', error);
        return { contas: [] };
      }
    },
    {
      enabled: !!estabelecimentoId,
      refetchInterval: 30000
    }
  );
  
  // ✅ Separar contas a pagar e a receber
  const todasContas = contasData?.contas || [];
  console.log('📊 Relatório Financeiro - Total de contas:', todasContas.length);
  console.log('📊 Relatório Financeiro - Contas:', todasContas);
  
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p style={{ color: '#666' }}>Carregando relatório financeiro...</p>
      </div>
    );
  }
  
  const contasPagar = todasContas.filter(c => c.tipo === 'pagar');
  const contasReceber = todasContas.filter(c => c.tipo === 'receber');
  
  // ✅ Contas pagas (tanto a pagar quanto a receber)
  const contasPagas = todasContas.filter(c => c.status === 'pago');
  const contasPendentes = todasContas.filter(c => c.status !== 'pago' && c.status !== 'cancelado');
  
  // ✅ Calcular totais CORRETOS
  // Total pago = soma de todas as contas pagas (a pagar e a receber)
  const totalPago = contasPagas.reduce((acc, c) => acc + (c.valorPago || c.valor || 0), 0);
  
  // A pagar = contas a pagar pendentes (valor - valorPago)
  const totalAPagar = contasPagar
    .filter(c => c.status !== 'pago' && c.status !== 'cancelado')
    .reduce((acc, c) => acc + (c.valor - (c.valorPago || 0)), 0);
  
  // A receber = contas a receber pendentes (valor - valorPago)
  const totalAReceber = contasReceber
    .filter(c => c.status !== 'pago' && c.status !== 'cancelado')
    .reduce((acc, c) => acc + (c.valor - (c.valorPago || 0)), 0);
  
  // Total pendente = a pagar - a receber (saldo líquido pendente)
  const totalPendente = totalAPagar - totalAReceber;
  
  console.log('💰 TOTAIS CALCULADOS:');
  console.log('   Total Pago: R$', totalPago.toFixed(2));
  console.log('   A Pagar: R$', totalAPagar.toFixed(2));
  console.log('   A Receber: R$', totalAReceber.toFixed(2));
  console.log('   Saldo Líquido: R$', totalPendente.toFixed(2));

  return (
    <div className="relatorio-card-premium">
      <div className="card-header-premium">
        <div>
          <h3>
            <DollarSign size={24} />
            Relatório Financeiro
          </h3>
          <p className="card-subtitle">Análise completa das contas financeiras</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {/* Total Pago */}
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <CheckCircle2 size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total Pago</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>R$ {totalPago.toFixed(2)}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>{contasPagas.length} conta(s)</div>
            </div>
          </div>
        </div>

        {/* A Pagar */}
        <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Clock size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>A Pagar</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>R$ {totalAPagar.toFixed(2)}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                {contasPagar.filter(c => c.status !== 'pago' && c.status !== 'cancelado').length} conta(s)
              </div>
            </div>
          </div>
        </div>

        {/* A Receber */}
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <TrendingUp size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>A Receber</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>R$ {totalAReceber.toFixed(2)}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                {contasReceber.filter(c => c.status !== 'pago' && c.status !== 'cancelado').length} conta(s)
              </div>
            </div>
          </div>
        </div>

        {/* Saldo Líquido Pendente */}
        <div style={{ 
          background: totalPendente >= 0 
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          color: 'white' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <DollarSign size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Saldo Líquido</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>
                {totalPendente >= 0 ? '-' : '+'}R$ {Math.abs(totalPendente).toFixed(2)}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                {totalPendente >= 0 ? 'A pagar' : 'A receber'}
              </div>
            </div>
          </div>
        </div>

        {/* Contas Pagas */}
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <FileText size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Contas Pagas</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{contasPagas.length}</div>
            </div>
          </div>
        </div>

        {/* Contas Pendentes */}
        <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', padding: '1.5rem', borderRadius: '12px', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AlertCircle size={28} />
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Contas Pendentes</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{contasPendentes.length}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente Relatório Entregadores
const RelatorioEntregadores = ({ estabelecimentoId }) => {
  return (
    <div className="relatorio-card-premium">
      <div className="card-header-premium">
        <div>
          <h3>
            <Truck size={24} />
            Relatório por Entregador
          </h3>
          <p className="card-subtitle">Em desenvolvimento</p>
        </div>
      </div>
      <EmptyState message="Relatório de entregadores em desenvolvimento" />
    </div>
  );
};

// Componentes Auxiliares
const LoadingState = () => (
  <div className="loading-state-premium">
    <div className="spinner-premium" />
    <p>Carregando relatório...</p>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="empty-state-premium">
    <FileText size={64} />
    <p>{message || 'Nenhum dado encontrado'}</p>
  </div>
);

// Componente Fluxo de Caixa Completo
const FluxoCaixaCompleto = ({
  fluxoCaixa,
  dataInicioFluxo,
  setDataInicioFluxo,
  dataFimFluxo,
  setDataFimFluxo,
  filtroOrigemFluxo,
  setFiltroOrigemFluxo
}) => {
  // Filtrar movimentações por origem
  const movimentacoesFiltradas = fluxoCaixa?.movimentacoes?.filter(mov => {
    if (filtroOrigemFluxo === 'todos') return true;
    if (filtroOrigemFluxo === 'vendas') return mov.origem === 'venda';
    if (filtroOrigemFluxo === 'contas') return mov.origem === 'conta_paga';
    if (filtroOrigemFluxo === 'manuais') return mov.origem === 'movimentacao_bancaria';
    if (filtroOrigemFluxo === 'lancamentos') return mov.origem === 'lancamento_financeiro';
    return true;
  }) || [];

  const resumo = fluxoCaixa?.resumo || {};

  return (
    <div className="fluxo-caixa-completo">
      {/* Filtros */}
      <div className="filtros-fluxo-caixa">
        <div className="filtro-group">
          <Calendar size={18} />
          <input
            type="date"
            value={dataInicioFluxo}
            onChange={(e) => setDataInicioFluxo(e.target.value)}
            className="input-premium"
          />
        </div>
        <div className="filtro-group">
          <Calendar size={18} />
          <input
            type="date"
            value={dataFimFluxo}
            onChange={(e) => setDataFimFluxo(e.target.value)}
            className="input-premium"
          />
        </div>
        <div className="filtro-group">
          <Filter size={18} />
          <select
            value={filtroOrigemFluxo}
            onChange={(e) => setFiltroOrigemFluxo(e.target.value)}
            className="select-premium"
          >
            <option value="todos">Todas as Origens</option>
            <option value="vendas">💰 Vendas</option>
            <option value="contas">💳 Contas Pagas</option>
            <option value="manuais">🏦 Movimentações Manuais</option>
            <option value="lancamentos">💵 Lançamentos</option>
          </select>
        </div>
      </div>

      {/* Cards de Resumo */}
      {resumo.totalEntradas !== undefined && (
        <div className="stats-grid-premium" style={{ marginBottom: '2rem' }}>
          <div className="stat-card-premium success">
            <div className="stat-icon-premium">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <span>Total de Entradas</span>
              <strong>R$ {resumo.totalEntradas?.toFixed(2) || '0.00'}</strong>
            </div>
          </div>
          <div className="stat-card-premium danger">
            <div className="stat-icon-premium">
              <TrendingDown size={24} />
            </div>
            <div className="stat-content">
              <span>Total de Saídas</span>
              <strong>R$ {resumo.totalSaidas?.toFixed(2) || '0.00'}</strong>
            </div>
          </div>
          <div className="stat-card-premium primary">
            <div className="stat-icon-premium">
              <DollarSign size={24} />
            </div>
            <div className="stat-content">
              <span>Saldo Final</span>
              <strong>R$ {resumo.saldoFinal?.toFixed(2) || fluxoCaixa?.saldoAtual?.toFixed(2) || '0.00'}</strong>
            </div>
          </div>
          <div className="stat-card-premium info">
            <div className="stat-icon-premium">
              <FileText size={24} />
            </div>
            <div className="stat-content">
              <span>Movimentações</span>
              <strong>{resumo.quantidadeMovimentacoes || movimentacoesFiltradas.length}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Movimentações */}
      <div className="relatorio-card-premium">
        <div className="card-header-premium">
          <div>
            <h3>
              <TrendingUp size={24} />
              Movimentações do Período
            </h3>
            <p className="card-subtitle">
              {fluxoCaixa?.periodo?.inicio && fluxoCaixa?.periodo?.fim
                ? `${new Date(fluxoCaixa.periodo.inicio).toLocaleDateString('pt-BR')} até ${new Date(fluxoCaixa.periodo.fim).toLocaleDateString('pt-BR')}`
                : 'Período selecionado'
              }
            </p>
          </div>
        </div>

        <div className="movimentacoes-lista-premium">
          {movimentacoesFiltradas.length > 0 ? (
            <div className="tabela-container-premium">
              <table className="tabela-premium">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Descrição</th>
                    <th>Origem</th>
                    <th>Categoria</th>
                    <th>Valor</th>
                    <th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {movimentacoesFiltradas.map((mov, idx) => (
                    <tr key={mov.id || idx} className={mov.tipo === 'entrada' ? 'entrada-row' : 'saida-row'}>
                      <td>
                        <div className="data-info">
                          <Calendar size={14} />
                          <span>{new Date(mov.data).toLocaleDateString('pt-BR')}</span>
                          <Clock size={14} />
                          <span>{new Date(mov.data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{mov.descricao}</strong>
                          {mov.cliente && (
                            <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>
                              Cliente: {mov.cliente}
                            </span>
                          )}
                          {mov.pedido && (
                            <span style={{ fontSize: '0.875rem', color: '#667eea' }}>
                              Pedido #{mov.pedido}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`origem-badge ${mov.origem}`}>
                          {mov.origem === 'venda' && '💰 Venda'}
                          {mov.origem === 'conta_paga' && '💳 Conta Paga'}
                          {mov.origem === 'movimentacao_bancaria' && '🏦 Movimentação'}
                        </span>
                      </td>
                      <td>
                        <span className="categoria-tag">{mov.categoria}</span>
                      </td>
                      <td>
                        <strong className={mov.tipo === 'entrada' ? 'valor-entrada' : 'valor-saida'}>
                          {mov.tipo === 'entrada' ? '+' : '-'} R$ {Math.abs(mov.valor).toFixed(2)}
                        </strong>
                        {mov.formaPagamento && (
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block' }}>
                            {mov.formaPagamento}
                          </span>
                        )}
                      </td>
                      <td>
                        <strong className="saldo-acumulado">
                          R$ {mov.saldo?.toFixed(2) || '0.00'}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state-premium">
              <TrendingUp size={64} />
              <p>Nenhuma movimentação encontrada no período selecionado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Lançamentos Completo
const LancamentosCompleto = ({ lancamentos, estabelecimentoId, refetchLancamentos, modalLancamento, setModalLancamento }) => {
  const queryClient = useQueryClient();

  const deletarMutation = useMutation(
    (id) => api.delete(`/api/financeiro/lancamentos/${id}`),
    {
      onSuccess: () => {
        toast.success('Lançamento deletado com sucesso!');
        queryClient.invalidateQueries('lancamentos-financeiro');
        queryClient.invalidateQueries('resumo-mensal');
        refetchLancamentos();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao deletar lançamento');
      }
    }
  );

  const receitas = lancamentos.filter(l => l.tipo === 'receita');
  const despesas = lancamentos.filter(l => l.tipo === 'despesa');
  const totalReceitas = receitas.reduce((acc, l) => acc + (l.valor || 0), 0);
  const totalDespesas = despesas.reduce((acc, l) => acc + (l.valor || 0), 0);

  return (
    <div className="lancamentos-completo">
      <div className="lancamentos-header">
        <div>
          <h3>💰 Lançamentos Financeiros</h3>
          <p>Receitas e despesas que afetam diretamente o valor líquido</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setModalLancamento(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Plus size={18} />
          Novo Lançamento
        </button>
      </div>

      {/* Cards de Resumo */}
      <div className="stats-grid-premium" style={{ marginBottom: '2rem' }}>
        <div className="stat-card-premium success">
          <div className="stat-icon-premium">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span>Total Receitas</span>
            <strong>R$ {totalReceitas.toFixed(2)}</strong>
            <small>{receitas.length} lançamento(s)</small>
          </div>
        </div>
        <div className="stat-card-premium danger">
          <div className="stat-icon-premium">
            <TrendingDown size={24} />
          </div>
          <div className="stat-content">
            <span>Total Despesas</span>
            <strong>R$ {totalDespesas.toFixed(2)}</strong>
            <small>{despesas.length} lançamento(s)</small>
          </div>
        </div>
        <div className="stat-card-premium primary">
          <div className="stat-icon-premium">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span>Saldo Lançamentos</span>
            <strong>R$ {(totalReceitas - totalDespesas).toFixed(2)}</strong>
            <small>Receitas - Despesas</small>
          </div>
        </div>
      </div>

      {/* Lista de Lançamentos */}
      <div className="relatorio-card-premium">
        <div className="card-header-premium">
          <div>
            <h3>
              <FileText size={24} />
              Todos os Lançamentos
            </h3>
            <p className="card-subtitle">{lancamentos.length} lançamento(s) cadastrado(s)</p>
          </div>
        </div>

        {lancamentos.length > 0 ? (
          <div className="tabela-container-premium">
            <table className="tabela-premium">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th>Forma Pagamento</th>
                  <th>Valor</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {lancamentos.map((lancamento) => (
                  <tr key={lancamento._id} className={lancamento.tipo === 'receita' ? 'entrada-row' : 'saida-row'}>
                    <td>
                      <div className="data-info">
                        <Calendar size={14} />
                        <span>{new Date(lancamento.data).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge-premium ${lancamento.tipo === 'receita' ? 'success' : 'danger'}`}>
                        {lancamento.tipo === 'receita' ? '💰 Receita' : '💸 Despesa'}
                      </span>
                    </td>
                    <td>
                      <strong>{lancamento.descricao}</strong>
                      {lancamento.observacoes && (
                        <small style={{ display: 'block', color: '#6b7280', fontSize: '0.875rem' }}>
                          {lancamento.observacoes}
                        </small>
                      )}
                    </td>
                    <td>
                      <span className="categoria-tag">{lancamento.categoria}</span>
                    </td>
                    <td>
                      <span className="forma-pagamento-tag">
                        {lancamento.formaPagamento === 'pix' && '💳 PIX'}
                        {lancamento.formaPagamento === 'dinheiro' && '💵 Dinheiro'}
                        {lancamento.formaPagamento === 'cartao_credito' && '💳 Crédito'}
                        {lancamento.formaPagamento === 'cartao_debito' && '💳 Débito'}
                        {lancamento.formaPagamento === 'transferencia' && '🏦 Transferência'}
                        {!['pix', 'dinheiro', 'cartao_credito', 'cartao_debito', 'transferencia'].includes(lancamento.formaPagamento) && lancamento.formaPagamento}
                      </span>
                    </td>
                    <td>
                      <strong className={lancamento.tipo === 'receita' ? 'valor-entrada' : 'valor-saida'}>
                        {lancamento.tipo === 'receita' ? '+' : '-'} R$ {Math.abs(lancamento.valor || 0).toFixed(2)}
                      </strong>
                    </td>
                    <td>
                      <button
                        className="btn-remove-item"
                        onClick={() => {
                          if (window.confirm('Tem certeza que deseja deletar este lançamento?')) {
                            deletarMutation.mutate(lancamento._id);
                          }
                        }}
                        disabled={deletarMutation.isLoading}
                        style={{ padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state-premium">
            <DollarSign size={64} />
            <p>Nenhum lançamento cadastrado</p>
            <button 
              className="btn btn-primary" 
              onClick={() => setModalLancamento(true)}
              style={{ marginTop: '1rem' }}
            >
              <Plus size={18} />
              Criar Primeiro Lançamento
            </button>
          </div>
        )}
      </div>

      {/* Modal de Lançamento */}
      {modalLancamento && (
        <ModalLancamento
          isOpen={modalLancamento}
          onClose={() => setModalLancamento(false)}
          estabelecimentoId={estabelecimentoId}
          refetchLancamentos={refetchLancamentos}
        />
      )}
    </div>
  );
};

// Modal de Lançamento
const ModalLancamento = ({ isOpen, onClose, estabelecimentoId, refetchLancamentos }) => {
  const [formData, setFormData] = useState({
    tipo: 'receita',
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    categoria: '',
    formaPagamento: 'dinheiro',
    observacoes: '',
    pagoAvista: true
  });
  const queryClient = useQueryClient();

  const criarMutation = useMutation(
    (data) => api.post('/api/financeiro/lancamentos', data),
    {
      onSuccess: () => {
        toast.success('✅ Lançamento criado com sucesso!');
        onClose();
        queryClient.invalidateQueries('lancamentos-financeiro');
        queryClient.invalidateQueries('resumo-mensal');
        refetchLancamentos();
        setFormData({
          tipo: 'receita',
          descricao: '',
          valor: '',
          data: new Date().toISOString().split('T')[0],
          categoria: '',
          formaPagamento: 'dinheiro',
          observacoes: '',
          pagoAvista: true
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao criar lançamento');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!estabelecimentoId) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    criarMutation.mutate({
      ...formData,
      estabelecimento: estabelecimentoId,
      valor: parseFloat(formData.valor)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-premium" onClick={onClose}>
      <div className="modal-content-premium modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-premium">
          <div>
            <h3>💰 Novo Lançamento Financeiro</h3>
            <p className="modal-subtitle">Receita ou despesa que afeta diretamente o valor líquido</p>
          </div>
          <button className="btn-close-premium" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body-premium">
          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>
                <DollarSign size={18} />
                Tipo *
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                required
                className="select-premium"
              >
                <option value="receita">💰 Receita</option>
                <option value="despesa">💸 Despesa</option>
              </select>
            </div>

            <div className="form-group-premium">
              <label>
                <FileText size={18} />
                Categoria *
              </label>
              <input
                type="text"
                value={formData.categoria}
                onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                required
                placeholder="Ex: Venda avulsa, Aluguel, etc"
              />
            </div>
          </div>

          <div className="form-group-premium">
            <label>
              <FileText size={18} />
              Descrição *
            </label>
            <input
              type="text"
              value={formData.descricao}
              onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              required
              placeholder="Ex: Venda de produto avulso"
            />
          </div>

          <div className="form-row-premium">
            <div className="form-group-premium">
              <label>
                <DollarSign size={18} />
                Valor (R$) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.valor}
                onChange={(e) => setFormData({...formData, valor: e.target.value})}
                required
                placeholder="0.00"
              />
            </div>

            <div className="form-group-premium">
              <label>
                <Calendar size={18} />
                Data *
              </label>
              <input
                type="date"
                value={formData.data}
                onChange={(e) => setFormData({...formData, data: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="form-group-premium">
            <label>
              <CreditCard size={18} />
              Forma de Pagamento *
            </label>
            <select
              value={formData.formaPagamento}
              onChange={(e) => setFormData({...formData, formaPagamento: e.target.value})}
              required
              className="select-premium"
            >
              <option value="dinheiro">💵 Dinheiro</option>
              <option value="pix">💳 PIX</option>
              <option value="cartao_credito">💳 Cartão de Crédito</option>
              <option value="cartao_debito">💳 Cartão de Débito</option>
              <option value="transferencia">🏦 Transferência Bancária</option>
            </select>
          </div>

          <div className="form-group-premium">
            <label>
              <FileText size={18} />
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              placeholder="Informações adicionais..."
              rows="3"
            />
          </div>

          <div className="modal-footer-premium">
            <button type="button" className="btn-cancel-premium" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-confirm-premium"
              disabled={criarMutation.isLoading}
            >
              {criarMutation.isLoading ? '⏳ Criando...' : '✅ Criar Lançamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Financeiro;
